import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CONSENT_KEY = 'equipr_cookie_consent';
const GA_ID = 'G-4LTRS7CVTV';
const HS_ID = '22520296';

const defaultConsent = {
  essential: true,
  analytics: false,
  marketing: false,
  timestamp: null,
};

const CookieConsentContext = createContext(null);

export const useCookieConsent = () => useContext(CookieConsentContext);

// --- Script injection helpers ---

function injectGA() {
  if (document.getElementById('ga-script')) return;
  // gtag.js loader
  const script = document.createElement('script');
  script.id = 'ga-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);
  // gtag init
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA_ID);
}

function removeGA() {
  const script = document.getElementById('ga-script');
  if (script) script.remove();
  // Clear GA cookies
  document.cookie.split(';').forEach((c) => {
    const name = c.trim().split('=')[0];
    if (name.startsWith('_ga') || name.startsWith('_gid') || name.startsWith('_gat')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  });
  delete window.gtag;
  delete window.dataLayer;
}

function injectHubSpot() {
  if (document.getElementById('hs-script-loader')) return;
  window.hsConversationsSettings = { loadImmediately: false };
  const script = document.createElement('script');
  script.id = 'hs-script-loader';
  script.async = true;
  script.defer = true;
  script.src = `//js.hs-scripts.com/${HS_ID}.js`;
  document.head.appendChild(script);
}

function removeHubSpot() {
  const script = document.getElementById('hs-script-loader');
  if (script) script.remove();
  // Clear HubSpot cookies
  document.cookie.split(';').forEach((c) => {
    const name = c.trim().split('=')[0];
    if (name.startsWith('__hs') || name === 'hubspotutk' || name.startsWith('__hstc') || name.startsWith('__hssc')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  });
}

// --- Provider ---

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(() => {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [bannerVisible, setBannerVisible] = useState(false);

  // Show banner if no consent recorded
  useEffect(() => {
    if (!consent || !consent.timestamp) {
      setBannerVisible(true);
    }
  }, [consent]);

  // Apply consent whenever it changes
  useEffect(() => {
    if (!consent || !consent.timestamp) return;

    if (consent.analytics) {
      injectGA();
    } else {
      removeGA();
    }

    if (consent.marketing) {
      injectHubSpot();
    } else {
      removeHubSpot();
    }
  }, [consent]);

  const saveConsent = useCallback((newConsent) => {
    const updated = { ...newConsent, essential: true, timestamp: new Date().toISOString() };
    setConsent(updated);
    localStorage.setItem(CONSENT_KEY, JSON.stringify(updated));
    setBannerVisible(false);
  }, []);

  const acceptAll = useCallback(() => {
    saveConsent({ essential: true, analytics: true, marketing: true });
  }, [saveConsent]);

  const declineAll = useCallback(() => {
    saveConsent({ essential: true, analytics: false, marketing: false });
  }, [saveConsent]);

  const openPreferences = useCallback(() => {
    setBannerVisible(true);
  }, []);

  return (
    <CookieConsentContext.Provider value={{ consent, bannerVisible, saveConsent, acceptAll, declineAll, openPreferences }}>
      {children}
    </CookieConsentContext.Provider>
  );
};
